<template>
  <v-card outlined class="pa-6">
    <h3 class="text-h6 font-weight-bold mb-4">Company Profile</h3>
    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      class="d-none"
      @change="onFileChange"
      :disabled="isSubmitting"
    />

    <v-progress-linear
      v-if="isSubmitting"
      indeterminate
      color="red darken-1"
      class="mb-4"
    />

    <v-form ref="formRef" @submit.prevent="onSubmit">
      <v-row dense>
        <!-- Logo Upload -->
        <v-col cols="12" md="3" class="d-flex flex-column align-center">
          <v-avatar size="120" class="mb-2" tile>
            <v-img :src="logoPreview" />
          </v-avatar>
          <v-btn outlined small @click="pickLogo" :disabled="isSubmitting">
            <v-icon left>mdi-image</v-icon> Upload Logo
          </v-btn>
          <small class="mt-2 text-caption text--disabled">
            Upload a .png or .jpg<br />(260×260) Max 1 MB.
          </small>
        </v-col>
        <!-- Details -->
        <v-col cols="12" md="9">
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.name"
                label="Company Name*"
                variant="outlined"
                dense
                :rules="[rules.required]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.company_type_id"
                :items="companyTypes"
                label="Company Type*"
                item-title="name"
                item-value="id"
                variant="outlined"
                dense
                :rules="[rules.required]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.city_id"
                :items="cities"
                label="City*"
                item-title="title"
                item-value="id"
               variant="outlined"
                dense
                :rules="[rules.required]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.industry_id"
                :items="industries"
                label="Industry*"
                item-title="name"
                item-value="id"
                variant="outlined"
                dense
                :rules="[rules.required]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.trade_license_no"
                label="Trade License No.*"
                variant="outlined"
                dense
                :rules="[rules.required]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.company_size_id"
                :items="companySizes"
                label="Company Size"
                item-title="range"
                item-value="id"
                variant="outlined"
                dense
                :rules="[rules.required]"
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="form.description"
                label="Description*"
                variant="outlined"
                dense
                rows="3"
                counter="5000"
                :rules="[rules.required]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.contact_name"
                label="Contact Name*"
                variant="outlined"
                dense
                :rules="[rules.required]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.phone_number"
                label="Phone Number*"
                variant="outlined"
                dense
                :rules="[rules.required]"
              />
            </v-col>
          </v-row>

          <h4 class="text-subtitle-1 font-weight-medium mt-6 mb-2">
            Social Profile
          </h4>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.website_url"
                label="Website"
                prepend-inner-icon="mdi-web"
                variant="outlined"
                dense
                :rules="[rules.url]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.youtube_url"
                label="YouTube"
                prepend-inner-icon="mdi-youtube"
                variant="outlined"
                dense
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.communication_email"
                label="Communication Email"
                prepend-inner-icon="mdi-email"
                variant="outlined"
                dense
                :rules="[rules.email]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.facebook_url"
                label="Facebook"
                prepend-inner-icon="mdi-facebook"
                variant="outlined"
                dense
                :rules="[rules.url]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.twitter_url"
                label="Twitter"
                prepend-inner-icon="mdi-twitter"
                variant="outlined"
                dense
                :rules="[rules.url]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.linkedin_url"
                label="LinkedIn"
                prepend-inner-icon="mdi-linkedin"
                variant="outlined"
                dense
                :rules="[rules.url]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.instagram_url"
                label="Instagram"
                prepend-inner-icon="mdi-instagram"
               variant="outlined"
                dense
                :rules="[rules.url]"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <div class="text-right mt-6">
        <v-btn
          type="submit"
          color="#b8860b"
          dark
          :loading="isSubmitting"
          :disabled="isSubmitting"
          >Save</v-btn
        >
      </div>
    </v-form>
  </v-card>
</template>

<script setup>
import { reactive, onMounted, ref, computed } from "vue";
import { useStore } from "vuex";
import placeholder from "@/assets/company.svg";
import { API_URL } from "@/helpers/api";

const store = useStore();

const props = defineProps({
  cities: Array,
  companyTypes: Array,
  industries: Array,
  companySizes: Array,
});
const emit = defineEmits(["save"]);
const formRef = ref(null);
const fileInput = ref(null);
const isSubmitting = ref(false);

const rules = {
  required: (v) => !!v || "This field is required",
  email: (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || "Invalid email",
  url: (v) =>
    !v ||
    /^(https?:\/\/)?[\w-]+(\.[\w-]+)+[/#?]?.*$/.test(v) ||
    "Must be a valid URL",
};

const logoPreview = computed(() => {
  if (!form.logo_path) return placeholder;
  if (form.logo_path.startsWith("blob:")) return form.logo_path;
  return `${API_URL}${form.logo_path}`;
});

const form = reactive({
  id: null,
  logo_path: "",
  logo_file: null,
  name: "",
  company_type_id: null,
  city_id: null,
  industry_id: null,
  trade_license_no: "",
  company_size_id: null,
  description: "",
  contact_name: "",
  phone_number: "",
  website_url: "",
  communication_email: "",
  youtube_url: "",
  facebook_url: "",
  twitter_url: "",
  linkedin_url: "",
  instagram_url: "",
});

function pickLogo() {
  if (!isSubmitting.value) fileInput.value.click();
}

function onFileChange(e) {
  const file = e.target.files[0];
  console.log("Picked file:", file);
  if (!file) return;
  form.logo_file = file;
  form.logo_path = URL.createObjectURL(file);
}

// async function onSubmit() {
//   if (!(await formRef.value.validate())) return;
//   isSubmitting.value = true;

//   const fd = new FormData();
//   if (form.id) {
//     fd.append("id", form.id);
//     //fd.append("_method", "PUT"); // if you’re spoofing PUT
//   }
//   if (form.logo_file) {
//     fd.append("logo", form.logo_file);
//     console.log("Logo picked", form.logo_file);
//   }

//   // for (let [key, value] of fd.entries()) {
//   //   console.log(key, value);
//   // }

//   // append all other fields except logo_file/logo_path…
//   for (const key in form) {
//     console.log(key);
//     console.log("show me whats inside form", form);
//     if (key === "logo_file" || key === "logo_path") continue;
//     fd.append(key, form[key] ?? "");
//   }

//   emit("save", fd);
//   isSubmitting.value = false;
// }

async function onSubmit() {
  if (!(await formRef.value.validate())) return;
  isSubmitting.value = true;

  // 1) Create the FormData
  const fd = new FormData();

  // 2) Mass-set all your plain fields at once
  Object.entries(form)
    .filter(([k]) => k !== "logo_file" && k !== "logo_path")
    .forEach(([key, value]) => {
      // use set() so you never accidentally get arrays for the same key
      fd.set(key, value == null ? "" : value);
    });

  // 3) Finally, set the file with its real name
  if (form.logo_file) {
    fd.set("logo", form.logo_file, form.logo_file.name);
    console.log("Logo appended:", fd.get("logo"));
  }

  // 4) Emit & done
  emit("save", fd);
  isSubmitting.value = false;
}

onMounted(async () => {
  const profile = await store.dispatch("companyProfile/fetchCompany");
  console.log("Whats inside this profile", profile);
  if (profile) {
    form.id = profile.id;
    form.name = profile.name;
    form.logo_path = profile.logo_path;
    form.company_type_id = profile.company_type_id;
    form.city_id = profile.city_id;
    form.industry_id = profile.industry_id;
    form.trade_license_no = profile.trade_license_no;
    form.company_size_id = profile.company_size_id;
    form.description = profile.description;
    form.contact_name = profile.contact_name;
    form.phone_number = profile.phone_number;
    form.website_url = profile.website_url;
    form.communication_email = profile.communication_email;
    form.youtube_url = profile.youtube_url;
    form.facebook_url = profile.facebook_url;
    form.twitter_url = profile.twitter_url;
    form.linkedin_url = profile.linkedin_url;
    form.instagram_url = profile.instagram_url;
  }
});
</script>

<style scoped></style>
